import React from 'react';
import { Form } from 'formik-antd';
import { Row, Col, Divider } from 'antd';
import {
  FormikProps,
  Formik,
  prepareDataForValidation,
  FieldArray,
} from 'formik';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import * as Yup from 'yup';

import FormItem from 'components/formik/FormItem';
import Checkbox from 'components/formik/Checkbox';
import FormikEffect from 'components/formik/FormikEffect';
import BillingInfoSectionArray from './BillingContactSectionArray';
import {
  initialBillingContactValidationSchema,
  submitBillingContactValidationSchema,
} from './BillingContactSection/BillingContactSection';
import { BillingContactModel } from 'api/backofficeApiClient';
import { countryUSAid } from 'constants/index';
import InternationalBillingInfoStateForm from './InternationalBillingInfoStateForm';
import BillingInfoStateForm from './BillingInfoStateForm';
import { checkIsCountriesWithStatesDropdown } from 'utils/validators/countryUtils';

export interface BillingInfoMainLocation {
  country?: number;
  state?: number;
  internationalStateName?: string;
  city?: string;
  internationalCityName?: string;
  address?: string;
  zip?: string;
}

type Props = {
  formRef: any;
  onChange: (formik: FormikProps<BillingInfoFormValues>) => void;
  mainLocation: BillingInfoMainLocation;
  initialValues?: BillingInfoFormValues;
};

export interface BillingInfoFormValues {
  billingContacts: BillingContactModel[];
  billingLocation: {
    locationSameAsMain: boolean;
    country?: number;
    internationalCityName?: string;
    internationalStateName?: string;
    state?: number;
    city: string;
    address: string;
    zip: string;
  };
}

export const isBillingInfoFormValidForDraft = (
  formik: FormikProps<BillingInfoFormValues>,
) => {
  const dataForValidation = prepareDataForValidation(formik.values);

  return draftValidationSchema.isValidSync(dataForValidation);
};

const initialValidationSchema = {
  billingContacts: Yup.array().of(
    Yup.object(initialBillingContactValidationSchema),
  ),
  billingLocation: {
    locationSameAsMain: Yup.boolean(),
    country: Yup.number().nullable(),
    state: Yup.string()
      .nullable()
      .when(['locationSameAsMain', 'country'], {
        is: (locationSameAsMain: boolean, country: number) =>
          !locationSameAsMain && checkIsCountriesWithStatesDropdown(country),
        then: Yup.string().trim().nullable(),
      }),
    internationalStateName: Yup.string().when(
      ['locationSameAsMain', 'country'],
      {
        is: (locationSameAsMain: boolean, country: number) =>
          !locationSameAsMain && !checkIsCountriesWithStatesDropdown(country),
        then: Yup.string()
          .trim()
          .max(120, 'State should be 120 characters maximum'),
      },
    ),
    city: Yup.string().when(['locationSameAsMain', 'country'], {
      is: (locationSameAsMain: boolean, country: number) =>
        !locationSameAsMain && checkIsCountriesWithStatesDropdown(country),
      then: Yup.string()
        .trim()
        .max(120, 'City should be 120 characters maximum'),
    }),
    internationalCityName: Yup.string().when(
      ['locationSameAsMain', 'country'],
      {
        is: (locationSameAsMain: boolean, country: number) =>
          !locationSameAsMain && !checkIsCountriesWithStatesDropdown(country),
        then: Yup.string()
          .trim()
          .max(120, 'City should be 120 characters maximum'),
      },
    ),
    address: Yup.string().when('locationSameAsMain', {
      is: (locationSameAsMain: boolean) => !locationSameAsMain,
      then: Yup.string()
        .trim()
        .max(120, 'Address should be 120 characters maximum'),
    }),
    zip: Yup.string().when('locationSameAsMain', {
      is: (locationSameAsMain: boolean) => !locationSameAsMain,
      then: Yup.string()
        .trim()
        .max(120, 'ZIP code should be 120 characters maximum'),
    }),
  },
};

const draftValidationSchema = Yup.object({
  billingContacts: initialValidationSchema.billingContacts,
  billingLocation: Yup.object(initialValidationSchema.billingLocation),
});

const submitValidationSchema = Yup.object({
  billingContacts: Yup.array().of(
    Yup.object(submitBillingContactValidationSchema),
  ),
  billingLocation: Yup.object({
    ...initialValidationSchema.billingLocation,
    country: Yup.number()
      .nullable()
      .when('locationSameAsMain', {
        is: (locationSameAsMain: boolean) => !locationSameAsMain,
        then: Yup.number().required('Field is required'),
      }),
    state: Yup.string()
      .nullable()
      .when(['locationSameAsMain', 'country'], {
        is: (locationSameAsMain: boolean, country: number) =>
          !locationSameAsMain && checkIsCountriesWithStatesDropdown(country),
        then: Yup.string().trim().required('Field is required'),
      }),
    internationalStateName: Yup.string().when(
      ['locationSameAsMain', 'country'],
      {
        is: (locationSameAsMain: boolean, country: number) =>
          !locationSameAsMain && !checkIsCountriesWithStatesDropdown(country),
        then: Yup.string()
          .trim()
          .required('Field is required')
          .max(120, 'State should be 120 characters maximum'),
      },
    ),
    city: Yup.string().when(['locationSameAsMain', 'country'], {
      is: (locationSameAsMain: boolean, country: number) =>
        !locationSameAsMain && checkIsCountriesWithStatesDropdown(country),
      then: Yup.string()
        .trim()
        .required('Field is required')
        .max(120, 'City should be 120 characters maximum'),
    }),
    internationalCityName: Yup.string().when(
      ['locationSameAsMain', 'country'],
      {
        is: (locationSameAsMain: boolean, country: number) =>
          !locationSameAsMain && !checkIsCountriesWithStatesDropdown(country),
        then: Yup.string()
          .trim()
          .required('Field is required')
          .max(120, 'City should be 120 characters maximum'),
      },
    ),
    address: Yup.string().when('locationSameAsMain', {
      is: (locationSameAsMain: boolean) => !locationSameAsMain,
      then: Yup.string()
        .trim()
        .required('Field is required')
        .max(120, 'Address should be 120 characters maximum'),
    }),
    zip: Yup.string().when('locationSameAsMain', {
      is: (locationSameAsMain: boolean) => !locationSameAsMain,
      then: Yup.string()
        .trim()
        .max(120, 'ZIP code should be 120 characters maximum'),
    }),
  }),
});

export default React.memo((props: Props) => {
  const {
    formRef,
    onChange,
    initialValues: predefinedInitialValues,
    mainLocation,
  } = props;

  const initialValues: BillingInfoFormValues = {
    billingContacts: [
      {
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
      },
    ],
    billingLocation: {
      locationSameAsMain: false,
      country: countryUSAid,
      state: undefined,
      internationalStateName: undefined,
      internationalCityName: undefined,
      city: '',
      address: '',
      zip: '',
    },
  };

  const handleBillingLocationSameAsMainChange = (
    event: CheckboxChangeEvent,
    formik: FormikProps<BillingInfoFormValues>,
  ) => {
    const isChecked = event.target.checked;

    const billingLocationCountry = isChecked ? mainLocation.country : undefined;

    const isInternationalCountry = !checkIsCountriesWithStatesDropdown(
      billingLocationCountry,
    );

    const billingLocationState = isChecked
      ? !isInternationalCountry
        ? mainLocation.state
        : ''
      : '';
    const billingLocationInternationalState = isChecked
      ? isInternationalCountry
        ? mainLocation.internationalStateName
        : ''
      : '';
    const billingLocationInternationalCity = isChecked
      ? isInternationalCountry
        ? mainLocation.internationalCityName
        : ''
      : '';
    const billingLocationCity = isChecked
      ? !isInternationalCountry
        ? mainLocation.city
        : ''
      : '';
    const billingLocationAddress = isChecked ? mainLocation.address : '';
    const billingLocationZip = isChecked ? mainLocation.zip : '';

    formik.setFieldValue(
      'billingLocation.country',
      billingLocationCountry,
      false,
    );
    formik.setFieldTouched('billingLocation.country', true, false);

    formik.setFieldValue('billingLocation.state', billingLocationState, false);
    formik.setFieldTouched('billingLocation.state', true, false);

    formik.setFieldValue('billingLocation.city', billingLocationCity, false);
    formik.setFieldTouched('billingLocation.city', true, false);

    formik.setFieldValue(
      'billingLocation.address',
      billingLocationAddress,
      false,
    );
    formik.setFieldTouched('billingLocation.address', true, false);

    formik.setFieldValue('billingLocation.zip', billingLocationZip, false);
    formik.setFieldTouched('billingLocation.zip', true, false);

    formik.setFieldValue(
      'billingLocation.internationalStateName',
      billingLocationInternationalState,
      false,
    );
    formik.setFieldTouched('billingLocation.internationalStateName', true, false);

    formik.setFieldValue(
      'billingLocation.internationalCityName',
      billingLocationInternationalCity,
      false,
    );
    formik.setFieldTouched('billingLocation.internationalCityName', true, false);

    setTimeout(() => {
      formik.validateForm();
    }, 0);
  };

  return (
    <Formik
      enableReinitialize
      innerRef={formRef}
      initialValues={predefinedInitialValues || initialValues}
      validationSchema={submitValidationSchema}
      onSubmit={() => {}}
    >
      {(formik) => (
        <>
          <FormikEffect onChange={onChange} />

          <Form layout="vertical">
            <div className="modal-title">Billing information</div>

            <FieldArray
              name="billingContacts"
              component={BillingInfoSectionArray as any}
            />

            <Divider className="modal-divider after-form" />
            <div className="modal-title">Billing address</div>

            <Row gutter={40}>
              <Col xs={24} md={24}>
                <FormItem
                  name="billingLocation.locationSameAsMain"
                  className="checkbox-wrapper"
                >
                  <Checkbox
                    name="billingLocation.locationSameAsMain"
                    onChange={(event) =>
                      handleBillingLocationSameAsMainChange(event, formik)
                    }
                  >
                    Same as main address
                  </Checkbox>
                </FormItem>
              </Col>

              {checkIsCountriesWithStatesDropdown(
                formik.values.billingLocation.locationSameAsMain
                  ? mainLocation.country
                  : formik.values.billingLocation.country,
              ) ? (
                <BillingInfoStateForm
                  formik={formik}
                  mainLocation={mainLocation}
                />
              ) : (
                <InternationalBillingInfoStateForm
                  formik={formik}
                  mainLocation={mainLocation}
                />
              )}
            </Row>
          </Form>
        </>
      )}
    </Formik>
  );
});
